import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Mail } from 'lucide-react';
import { getCampaignStatusStyle, getCampaignStatusLabel } from '../../../constants/campaignStatus';

// Campaign item for display - supports both API campaigns and transformed data
interface CampaignDisplayItem {
  id: string;
  name: string;
  status: string;
  recipients_count?: number;
  stats?: { sent: number };
}

interface CampaignsSectionProps {
  campaigns: CampaignDisplayItem[];
  onNewCampaign: () => void;
}

export const CampaignsSection: React.FC<CampaignsSectionProps> = ({
  campaigns,
  onNewCampaign,
}) => {
  const navigate = useNavigate();

  if (campaigns.length === 0) return null;

  const displayCampaigns = campaigns.slice(0, 4);

  return (
    <section className="mb-10">
      <div className="bg-white border border-neutral-200 rounded-2xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[13px] font-medium text-neutral-400 uppercase tracking-wider">
            Campaigns
          </h2>
          <button
            onClick={() => navigate('/campaigns')}
            className="text-[11px] text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            Manage
          </button>
        </div>

        <div className="hidden sm:flex items-center gap-4 px-3 -mx-3 pb-2 mb-1 border-b border-neutral-100">
          <span className="flex-1 text-[10px] font-medium text-neutral-400 uppercase tracking-wide">Name</span>
          <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-wide w-14 text-right">Recipients</span>
          <span className="text-[10px] font-medium text-neutral-400 uppercase tracking-wide w-16 text-right">Status</span>
        </div>

        <div className="space-y-0">
          {displayCampaigns.map((campaign) => (
            <Link
              key={campaign.id}
              to={`/campaigns/${campaign.id}`}
              className="group flex items-center gap-3 sm:gap-4 py-2.5 px-2 sm:px-3 -mx-2 sm:-mx-3 rounded-lg hover:bg-neutral-50 transition-colors"
            >
              <div className="flex-1 flex items-center gap-2 sm:gap-2.5 min-w-0">
                <div className="w-6 h-6 bg-neutral-100 rounded flex items-center justify-center flex-shrink-0">
                  <Mail className="w-3 h-3 text-neutral-400" />
                </div>
                <span className="text-[13px] text-neutral-900 truncate">
                  {campaign.name}
                </span>
              </div>
              <span className="text-[13px] text-neutral-500 tabular-nums w-10 sm:w-14 text-right">
                {campaign.recipients_count ?? campaign.stats?.sent ?? 0}
              </span>
              <span className={`text-[11px] font-medium w-14 sm:w-16 text-right ${getCampaignStatusStyle(campaign.status)}`}>
                {getCampaignStatusLabel(campaign.status)}
              </span>
            </Link>
          ))}
        </div>

        <div className="mt-3 pt-3 border-t border-neutral-100">
          <button
            onClick={onNewCampaign}
            className="flex items-center gap-2 text-[13px] text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>New campaign</span>
          </button>
        </div>
      </div>
    </section>
  );
};
